// Workspace list toolbar — Refresh · IO menu (export / import wizard / template / AI guide) · New task.
import { useState } from "react";
import { Download, Upload, Plus, RefreshCw, ChevronDown, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { isMgmt } from "../_shared";
import { useAuth } from "../../../context/AuthContext";
import ImportWizard from "./ImportWizard";

const API = process.env.REACT_APP_BACKEND_URL + "/api/workspace/tasks";

export async function download(path, fallbackName) {
  try {
    const r = await fetch(path.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${path}` : `${API}${path}`, { credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("talmia_token") || ""}` } });
    if (!r.ok) throw new Error(`${r.status}`);
    const name = (r.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] || fallbackName;
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${name}`);
  } catch (e) { toast.error("Download failed", { description: e.message }); }
}

const Item = ({ onClick, icon: Icon, tone, children, testid }) => (
  <button onClick={onClick} className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] text-slate-200 hover:bg-white/5 text-left" data-testid={testid}>
    <Icon className={`w-3.5 h-3.5 ${tone}`} />{children}
  </button>
);

export default function ListToolbar({ onRefresh, onNew, newLabel = "New task", onImported, teamId }) {
  const { user } = useAuth();
  const mgmt = isMgmt(user);
  const [menu, setMenu] = useState(false);
  const [wizard, setWizard] = useState(false);
  const go = (fn) => () => { setMenu(false); fn(); };
  return (
    <div className="flex items-center gap-2" data-testid="ws-list-toolbar">
      {onRefresh && (
        <button onClick={onRefresh} className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-white/5" title="Refresh" data-testid="ws-list-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      )}
      <div className="relative">
        <button onClick={() => setMenu((v) => !v)} data-testid="ws-list-io-menu"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] text-slate-200 text-[11px] font-mono uppercase tracking-widest">
          <Download className="w-3 h-3" /> Import / Export <ChevronDown className="w-3 h-3" />
        </button>
        {menu && (
          <div role="menu" className="absolute right-0 mt-1 min-w-[260px] rounded-lg border border-white/10 bg-[#0F1420]/95 backdrop-blur-xl shadow-2xl overflow-hidden z-50" onMouseLeave={() => setMenu(false)}>
            <Item onClick={go(() => download("/export.csv?scope=mine", "my-tasks.csv"))} icon={Download} tone="text-sky-300" testid="ws-export-mine">Export my tasks (CSV)</Item>
            {teamId && mgmt && <Item onClick={go(() => download(`/export.csv?scope=all&team_id=${teamId}`, "team-tasks.csv"))} icon={Download} tone="text-teal-300" testid="ws-export-scoped">Export this team</Item>}
            {mgmt && <Item onClick={go(() => download("/export.csv?scope=all", "all-tasks.csv"))} icon={Download} tone="text-amber-300" testid="ws-export-all">Export all company tasks</Item>}
            <Item onClick={go(() => download("/api/crm/activities-io/export.csv", "crm-activities.csv"))} icon={Download} tone="text-orange-300" testid="ws-export-activities">{mgmt ? "Export all CRM activities" : "Export my CRM activities"}</Item>
            {mgmt && <>
              <div className="border-t border-white/5" />
              <Item onClick={go(() => setWizard("tasks"))} icon={Upload} tone="text-fuchsia-300" testid="ws-import-open">Import workspace tasks… (preview first)</Item>
              <Item onClick={go(() => setWizard("activities"))} icon={Upload} tone="text-orange-300" testid="ws-import-activities-open">Import CRM activities… (preview first)</Item>
              <Item onClick={go(() => download("/import/template.csv", "talmia-task-import-template.csv"))} icon={FileSpreadsheet} tone="text-emerald-300" testid="ws-import-template">Download import template</Item>
              <Item onClick={go(() => download("/import/guide.md", "talmia-task-import-guide.md"))} icon={FileText} tone="text-violet-300" testid="ws-import-guide">Import guide (for people & AI)</Item>
            </>}
          </div>
        )}
      </div>
      {onNew && (
        <button onClick={onNew} data-testid="ws-list-new"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 font-mono uppercase tracking-widest text-[10.5px] hover:bg-amber-500/20">
          <Plus className="w-3 h-3" /> {newLabel}
        </button>
      )}
      {wizard && <ImportWizard kind={wizard} onClose={() => setWizard(false)} onDone={(r) => { setWizard(false); onImported?.(r); }} />}
    </div>
  );
}
